import type { FieldMergeFunction, Reference } from '@apollo/client';
import type { StoreObject } from '@apollo/client/cache';

type Appraisal = Reference | StoreObject;

function competencyId(
	appraisal: Appraisal,
	readField: (fieldName: string, from?: Reference | StoreObject) => unknown,
): string | undefined {
	const competency = readField('competency', appraisal);

	if (!competency || typeof competency !== 'object') {
		return undefined;
	}

	const id = readField('id', competency as Reference | StoreObject);

	return typeof id === 'string' ? id : undefined;
}

/**
 * `CompetencyAppraisal` has no id, so Apollo stores the list inside `JobPosition`
 * and replaces it on every write. The competencies query and the feedback query
 * each omit the other's fields; merging by competency id keeps both.
 */
export const mergeCompetencyAppraisals: FieldMergeFunction<
	readonly Appraisal[],
	readonly Appraisal[]
> = (existing, incoming, { readField, mergeObjects }) => {
	if (!existing?.length || !incoming) {
		return incoming;
	}

	const existingByCompetencyId = new Map<string, Appraisal>();

	for (const appraisal of existing) {
		const id = competencyId(appraisal, readField);

		if (id) {
			existingByCompetencyId.set(id, appraisal);
		}
	}

	return incoming.map((appraisal) => {
		const id = competencyId(appraisal, readField);
		const previous = id ? existingByCompetencyId.get(id) : undefined;

		return previous ? mergeObjects(previous, appraisal) : appraisal;
	});
};
